import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Target, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import { PillarCard } from '../components/PillarCard';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { PillarRating, PILLARS, AlignmentArea } from '../types/alignment';
import { supabase } from '../lib/supabase';

export function AlignmentAuditAreaSelect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditId, ratings } = location.state || {};
  const [selectedArea, setSelectedArea] = useState<AlignmentArea | null>(null);

  if (!auditId || !ratings) {
    navigate('/alignment-audit/start');
    return null;
  }

  const lowestRatedPillar = PILLARS.reduce((lowest, pillar) => {
    const currentRating = ratings[pillar.ratingKey] as PillarRating;
    const lowestRating = ratings[lowest.ratingKey] as PillarRating;
    return currentRating < lowestRating ? pillar : lowest;
  }, PILLARS[0]);

  const handleNext = async () => {
    if (!selectedArea) return;

    const selectedPillar = PILLARS.find(p => p.id === selectedArea);
    if (!selectedPillar) return;

    const { error } = await supabase
      .from('alignment_audits')
      .update({ selected_area: selectedPillar.name })
      .eq('id', auditId);

    if (error) {
      console.error('Error updating audit:', error);
      return;
    }

    navigate('/alignment-audit/diagnostic', {
      state: { auditId, selectedArea: selectedPillar }
    });
  };

  return (
    <ProtectedLayout>
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 rounded-full mb-4">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Select Your Focus Area
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              You've identified the friction. Now choose which area you want to bring into alignment.
              We recommend starting with your lowest-rated area, but the choice is yours.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {PILLARS.map((pillar) => (
              <PillarCard
                key={pillar.id}
                pillar={pillar}
                rating={ratings[pillar.ratingKey] as PillarRating}
                isRecommended={pillar.id === lowestRatedPillar.id}
                isSelected={selectedArea === pillar.id}
                onClick={() => setSelectedArea(pillar.id)}
              />
            ))}
          </div>

          <div className="flex justify-between items-center">
            <Button
              onClick={() => navigate('/alignment-audit/start')}
              variant="outline"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>

            <Button
              onClick={handleNext}
              disabled={!selectedArea}
              className="px-8 py-4 text-lg"
            >
              Start Audit for {selectedArea && PILLARS.find(p => p.id === selectedArea)?.name}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}
